/** Consent-gated storefront vendors. Configuration is owned by the Liquid JSON script. */
export const TRACKING_CONFIG_ID = 'solstice-tracking-config';
export const RX_PRIVATE_SELECTOR = 'solstice-rx-configurator, solstice-rx-completion, [data-upload-filename], [data-rx-lens-details], .rx-lens-details';
const MAX_PRIVACY_ATTEMPTS = 8;

export function readTrackingConsent(privacy) {
  if (typeof privacy?.analyticsProcessingAllowed !== 'function' || typeof privacy?.marketingAllowed !== 'function') {
    return { analytics: false, marketing: false };
  }
  const allowed = (method) => {
    try { return privacy?.[method]?.() === true; } catch { return false; }
  };
  return { analytics: allowed('analyticsProcessingAllowed'), marketing: allowed('marketingAllowed') };
}

export function googleConsent({ analytics, marketing }) {
  return {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  };
}

/** Do not forward prescription/file values or URL query tokens in custom event properties. */
export function sanitizeTrackingEvent(event, projectKey) {
  const sensitive = /prescription|optician|prism|pupillary|(^|_)(sph|cyl|axi|axis|pd|pdf|file|filename|note|notes|token|email|phone|address|body|value)(_|$)/i;
  const clean = (value) => {
    if (Array.isArray(value)) return value.map(clean);
    if (!value || typeof value !== 'object') return value;
    const result = {};
    for (const [key, item] of Object.entries(value)) {
      if (sensitive.test(key.replace(/([a-z0-9])([A-Z])/g, '$1_$2'))) continue;
      if (typeof item === 'string' && /url|href|referrer/i.test(key)) {
        try { const url = new URL(item); url.search = ''; url.hash = ''; result[key] = url.href; }
        catch { /* Drop non-absolute URLs rather than exposing embedded values. */ }
      } else result[key] = clean(item);
    }
    return result;
  };
  const properties = clean(event.properties || {});
  // PostHog uses this exact property for ingestion. Preserve only our public project key.
  if (projectKey && event.properties?.token === projectKey) properties.token = projectKey;
  return { ...event, properties };
}

export function readTrackingConfig(doc) {
  try {
    const value = JSON.parse(doc.getElementById(TRACKING_CONFIG_ID)?.textContent || 'null');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  } catch { return null; }
}

export function createTrackingRuntime(config, win, doc) {
  if (!config || config.designMode === true || win.Shopify?.designMode === true) return null;
  let consent = { analytics: false, marketing: false };
  let previous = null;
  let attempts = 0;
  let pending = false;
  let retryTimer;
  let posthogStarted = false;
  let posthogReady = false;
  let posthogAllowed = null;
  let clarityStarted = false;
  let gtmStarted = false;
  let started = false;
  const scripts = new Set();
  const command = (name, ...args) => {
    try { win[name]?.(...args); } catch { /* A vendor must never break storefront behavior. */ }
  };
  const loadScript = (name, src) => {
    const id = `solstice-tracking-${name}`;
    if (scripts.has(name) || doc.getElementById(id)) return;
    scripts.add(name);
    const script = doc.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    doc.head.appendChild(script);
  };
  const privateControls = `${RX_PRIVATE_SELECTOR}, input, textarea, select`;
  const maskPrivateDom = (root = doc) => {
    const elements = [...root.querySelectorAll(privateControls)];
    if (root.matches?.(privateControls)) elements.unshift(root);
    for (const element of elements) {
      element.setAttribute('data-clarity-mask', 'true');
      element.removeAttribute('data-clarity-unmask');
      element.classList.add(element.matches(RX_PRIVATE_SELECTOR) ? 'ph-no-capture' : 'ph-ignore-input');
    }
  };
  const syncPosthog = () => {
    if (!posthogReady || posthogAllowed === consent.analytics) return;
    posthogAllowed = consent.analytics;
    if (consent.analytics) {
      win.posthog.opt_in_capturing({ captureEventName: false });
    } else {
      win.posthog.opt_out_capturing();
    }
  };
  const loadPosthog = () => {
    if (posthogStarted || !config.posthogKey) return;
    let host;
    try {
      host = new URL(config.posthogHost || 'https://us.i.posthog.com');
      if (host.protocol !== 'https:') return;
    } catch { return; }
    posthogStarted = true;
    // The array.js entrypoint consumes __SV, _i and queued method arrays.
    const ph = win.posthog = win.posthog || [];
    if (!ph.init) {
      ph._i = [];
      ph.__SV = 1;
      ph.people = [];
      for (const method of ['capture', 'identify', 'register', 'register_once', 'reset', 'opt_in_capturing', 'opt_out_capturing', 'onSessionId']) {
        ph[method] = (...args) => ph.push([method, ...args]);
      }
      ph.init = (key, options) => ph._i.push([key, options, 'posthog']);
    }
    ph.init(config.posthogKey, {
      api_host: host.origin,
      defaults: '2025-11-30',
      opt_out_capturing_by_default: true,
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: { css_selector_ignorelist: [RX_PRIVATE_SELECTOR] },
      mask_all_text: true,
      mask_all_element_attributes: true,
      enable_recording_console_log: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        blockSelector: `${RX_PRIVATE_SELECTOR}, .ph-no-capture, img, video, canvas`,
        recordHeaders: false,
        recordBody: false,
        maskCapturedNetworkRequestFn: () => null,
      },
      before_send: (event) => readTrackingConsent(win.Shopify?.customerPrivacy).analytics ? sanitizeTrackingEvent(event, config.posthogKey) : null,
      loaded: (instance) => {
        win.posthog = instance;
        posthogReady = true;
        consent = readTrackingConsent(win.Shopify?.customerPrivacy);
        syncPosthog();
        if (consent.analytics) instance.capture('$pageview');
      },
    });
    host.hostname = host.hostname.replace('.i.posthog.com', '-assets.i.posthog.com');
    loadScript('posthog', `${host.origin}/static/array.js`);
  };
  const applyConsent = () => {
    consent = readTrackingConsent(win.Shopify?.customerPrivacy);
    const changed = !previous || previous.analytics !== consent.analytics || previous.marketing !== consent.marketing;
    if (!changed) return;
    const wasAnalyticsAllowed = previous?.analytics === true;
    const clarityWasStarted = clarityStarted;
    previous = { ...consent };
    if (config.gtmId) command('gtag', 'consent', 'update', googleConsent(consent));
    if (consent.analytics) {
      maskPrivateDom();
      loadPosthog();
      if (config.clarityId && !clarityStarted) {
        clarityStarted = true;
        win.clarity = win.clarity || function (...args) { (win.clarity.q = win.clarity.q || []).push(args); };
      }
    }
    syncPosthog();
    if (clarityStarted) {
      command('clarity', 'consentv2', { analytics_Storage: consent.analytics ? 'granted' : 'denied', ad_Storage: consent.marketing ? 'granted' : 'denied' });
      if (!consent.analytics) command('clarity', 'stop');
      else if (clarityWasStarted && !wasAnalyticsAllowed) command('clarity', 'start');
      if (!clarityWasStarted) loadScript('clarity', `https://www.clarity.ms/tag/${encodeURIComponent(config.clarityId)}`);
    }
    if (consent.analytics && consent.marketing && config.gtmId && !gtmStarted) {
      gtmStarted = true;
      win.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      loadScript('gtm', `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(config.gtmId)}`);
    }
  };
  const scheduleRetry = () => {
    if (attempts < MAX_PRIVACY_ATTEMPTS && retryTimer === undefined) {
      retryTimer = win.setTimeout(() => { retryTimer = undefined; requestPrivacy(); }, 500);
    }
  };
  const requestPrivacy = () => {
    const privacy = win.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed === 'function' && typeof privacy?.marketingAllowed === 'function') {
      applyConsent(); return;
    }
    if (pending || attempts >= MAX_PRIVACY_ATTEMPTS) return;
    attempts++;
    if (typeof win.Shopify?.loadFeatures !== 'function') { scheduleRetry(); return; }
    pending = true;
    const timeout = win.setTimeout(() => { pending = false; scheduleRetry(); }, 3000);
    try {
      win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        win.clearTimeout(timeout);
        pending = false;
        if (!error) applyConsent();
        if (error || typeof win.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function' || typeof win.Shopify?.customerPrivacy?.marketingAllowed !== 'function') scheduleRetry();
      });
    } catch { win.clearTimeout(timeout); pending = false; scheduleRetry(); }
  };
  return {
    start() {
      if (started) return;
      started = true;
      if (config.gtmId) {
        win.dataLayer = win.dataLayer || [];
        win.gtag = win.gtag || function () { win.dataLayer.push(arguments); };
        command('gtag', 'consent', 'default', googleConsent(consent));
      }
      maskPrivateDom();
      if (win.MutationObserver) {
        const observer = new win.MutationObserver((records) => {
          const roots = new Set();
          for (const record of records) {
            for (const node of record.addedNodes) if (node.nodeType === 1) roots.add(node);
          }
          for (const root of roots) {
            let ancestor = root.parentElement;
            while (ancestor && !roots.has(ancestor)) ancestor = ancestor.parentElement;
            if (!ancestor) maskPrivateDom(root);
          }
        });
        observer.observe(doc.documentElement, { childList: true, subtree: true });
      }
      doc.addEventListener('visitorConsentCollected', () => {
        // Read the authoritative API, never infer a grant from event payloads.
        applyConsent();
        requestPrivacy();
      });
      requestPrivacy();
    },
  };
}

export function startTracking(win, doc) {
  if (win.__solsticeTrackingRuntime) return win.__solsticeTrackingRuntime;
  const runtime = createTrackingRuntime(readTrackingConfig(doc), win, doc);
  if (runtime) { win.__solsticeTrackingRuntime = runtime; runtime.start(); }
  return runtime;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') startTracking(window, document);
